import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore';
import { db } from '../../config';
import { registerStatus, userRole } from '../../../../constants/enum';

export const usersRef = collection(db, 'users');

const userDocument = (uid: string): DocumentReference<DocumentData> => doc(usersRef, uid);

const userSnapshot = async (uid: string): Promise<DocumentSnapshot> =>
  await getDoc(userDocument(uid));

/**
 * ユーザー情報を取得
 */
export const getUser = async (uid: string): Promise<DocumentData | undefined> => {
  try {
    const user = await userSnapshot(uid);
    const userData = user.data();
    return userData;
  } catch (error) {
    return undefined;
  }
};

/**
 * 全ユーザーを取得
 */
export const getAllUsers = async (): Promise<DocumentData[]> => {
  const usersQuery: Query<DocumentData> = query(
    usersRef,
    where('register_status', '!=', registerStatus.deleted),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
  return querySnapshot.docs.map(doc => doc.data());
};

/**
 * 会社IDでフィルタリングしたユーザーを取得
 */
export const getUsersByCompanyId = async (companyId: string): Promise<DocumentData[]> => {
  const usersQuery: Query<DocumentData> = query(
    usersRef,
    where('company_id', '==', companyId),
    where('register_status', '!=', registerStatus.deleted),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
  return querySnapshot.docs.map(doc => doc.data());
};

/**
 * 運営ユーザーのみを取得
 */
export const getOperatorUsers = async (): Promise<DocumentData[]> => {
  // 運営管理者を取得
  const adminQuery: Query<DocumentData> = query(
    usersRef,
    where('role', '==', userRole.admin),
    where('register_status', '!=', registerStatus.deleted),
  );
  const adminSnapshot: QuerySnapshot<DocumentData> = await getDocs(adminQuery);

  // 運営オペレーターを取得
  const operatorQuery: Query<DocumentData> = query(
    usersRef,
    where('role', '==', userRole.operator),
    where('register_status', '!=', registerStatus.deleted),
  );
  const operatorSnapshot: QuerySnapshot<DocumentData> = await getDocs(operatorQuery);

  // 両方の結果を結合
  return [
    ...adminSnapshot.docs.map(doc => doc.data()),
    ...operatorSnapshot.docs.map(doc => doc.data()),
  ];
};

/**
 * 有効なユーザーのみ取得
 */
export const getValidUsers = async (): Promise<DocumentData[]> => {
  const usersQuery: Query<DocumentData> = query(
    usersRef,
    where('register_status', '==', registerStatus.finished),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
  return querySnapshot.docs.map(doc => doc.data());
};

/**
 * メールアドレスからユーザーを取得
 */
export const userFindByEmail = async (email: string): Promise<boolean> => {
  const userQuery = query(usersRef, where('email', '==', email));
  const { empty, size } = await getDocs(userQuery);

  return !(empty || size >= 2);
};

export interface UserData {
  email: string;
  register_status?: number;
}

/**
 * メールアドレスからユーザーの登録状態を取得
 */
export const getUserRegisterStatusByEmail = async (email: string): Promise<UserData | null> => {
  const userQuery = await query(usersRef, where('email', '==', email));
  const querySnapshot = await getDocs(userQuery);

  if (querySnapshot.empty || querySnapshot.size >= 2) {
    return null; // ユーザーが見つからないか、複数のユーザーが見つかった場合
  }

  // 最初のドキュメントのデータを取得
  const userData = querySnapshot.docs[0].data() as UserData;
  return userData;
};

/**
 * メールアドレスからユーザーの存在確認と登録状態を一括取得
 * userFindByEmail + getUserRegisterStatusByEmail を1回のクエリで実行
 */
export const getUserByEmail = async (
  email: string,
): Promise<{ exists: boolean; userData: UserData | null }> => {
  const userQuery = query(usersRef, where('email', '==', email));
  const querySnapshot = await getDocs(userQuery);

  if (querySnapshot.empty || querySnapshot.size >= 2) {
    return { exists: false, userData: null };
  }

  const userData = querySnapshot.docs[0].data() as UserData;
  return { exists: true, userData };
};
